/**
 * Trace driven animations, the Babylon half.
 *
 * A driven animation is a clip of a file whose TIME is a morph target weight
 * of the same file: `animations[k].extras.trace.driver = { node, target }`
 * (`utils/drivenAnimation.ts`). It is never played by time and is not in the
 * object's clip list (`SceneObject.drivenAnimationClips`). Every frame, after
 * every time-based and bound channel has been evaluated
 * (`scene.onAfterAnimationsObservable`), its driver is read and the clip is
 * evaluated at
 *
 * ```
 * t = clamp(driverValue, input.min, input.max)
 * ```
 *
 * in the clip's own time units. So a performance bound onto an avatar's hidden
 * `face` carrier drives, in the SAME frame, whatever the avatar author hung on
 * a weight: an orb's glow (`KHR_materials_emissive_strength`), a texture
 * scroll (`KHR_texture_transform`), a colour, a node's scale.
 *
 * The driver resolves once per mount, by name, in the file the clip comes
 * from. An unresolvable driver leaves the clip inert and is reported once.
 */

import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { SceneObject } from "../../core/SceneObject";
import { Logger } from "../../utils/Logger";
import { getAnimationDriver, type AnimationDriverStamp } from "../../utils/drivenAnimation";
import { instanceView, primitivesOf } from "./bindAnimationSources";

/** Diagnostic code for a driver that does not resolve. */
export const DRIVEN_ANIMATION_DRIVER_UNRESOLVED = "driven-animation-driver-unresolved";

/** One driven animation of an object, as mounted. */
export interface DrivenAnimationReport {
    /** The group's name (the clip's name in its file). */
    clip: string;
    /** The driver, as `node/target` (e.g. `face/jawOpen`). */
    driver: string;
    /** Whether the driver resolved; when false the clip is inert. */
    resolved: boolean;
    problem?: string;
}

interface Driven {
    group: AnimationGroup;
    stamp: AnimationDriverStamp;
    read: () => number;
    last: number;
}

const reports = new WeakMap<SceneObject, DrivenAnimationReport[]>();
const passes = new WeakMap<SceneObject, () => void>();

/** The last mount's report for `object` (empty when it has no driven animations). */
export function getDrivenAnimationReport(object: SceneObject): readonly DrivenAnimationReport[] {
    return reports.get(object) ?? [];
}

function release(object: SceneObject): void {
    passes.get(object)?.();
    passes.delete(object);
}

/** A reader for the driver's current value, or why there is none. */
function driverReader(object: SceneObject, stamp: AnimationDriverStamp): { read: () => number } | { problem: string } {
    const view = instanceView(object);
    if (!view) return { problem: "the object has no loaded glTF instance" };
    if ("error" in view) return { problem: view.error };
    const index = view.names.nodeByName.get(stamp.node);
    const node = index !== undefined ? view.nodes.get(index) : undefined;
    if (index === undefined || !node) return { problem: `no node is named "${stamp.node}"` };
    const mesh = view.names.nodeMesh[index];
    const j = mesh !== undefined ? view.names.morphTargetNames[mesh]?.indexOf(stamp.target) ?? -1 : -1;
    const primitive = (view.primitives.get(index) ?? primitivesOf(node))[0] as Mesh | undefined;
    const manager = primitive?.morphTargetManager;
    if (j < 0 || !manager || j >= manager.numTargets) return { problem: `"${stamp.node}" has no morph target named "${stamp.target}"` };
    const target = manager.getTarget(j);
    return { read: () => target.influence };
}

/** Put a driven group at clip time `t` (seconds), paused. */
function evaluate(group: AnimationGroup, t: number): void {
    if (!group.isStarted) {
        group.start(false, 1, 0, group.to);
        group.pause();
    } else if (group.isPlaying) {
        group.pause();
    }
    const fps = group.targetedAnimations[0]?.animation.framePerSecond ?? 60;
    group.goToFrame(Math.max(0, Math.min(t * fps, group.to)));
}

/**
 * Mount `object`'s driven animations: resolve each driver in the object's
 * instance, and evaluate every driven clip each frame from it. Replaces
 * whatever an earlier mount registered. Called whenever the object's
 * components are (re)built, after its bindings (`SceneCreation.applyComponents`).
 */
export function mountDrivenAnimations(object: SceneObject): void {
    release(object);
    const groups = object.drivenAnimationClips;
    if (groups.length === 0) {
        reports.delete(object);
        return;
    }
    const report: DrivenAnimationReport[] = [];
    const driven: Driven[] = [];
    for (const group of groups) {
        const stamp = getAnimationDriver(group);
        if (!stamp) continue;
        const driver = `${stamp.node}/${stamp.target}`;
        const reader = driverReader(object, stamp);
        if ("problem" in reader) {
            report.push({ clip: group.name, driver, resolved: false, problem: reader.problem });
            Logger.warn(`[DrivenAnimation] ${DRIVEN_ANIMATION_DRIVER_UNRESOLVED}: "${object.name}" clip "${group.name}": `
                + `driver ${driver}: ${reader.problem} — the clip stays inert.`);
            continue;
        }
        report.push({ clip: group.name, driver, resolved: true });
        driven.push({ group, stamp, read: reader.read, last: NaN });
    }
    reports.set(object, report);
    if (driven.length === 0) return;

    const pass = (): void => {
        if (object.transform.isDisposed()) {
            release(object);
            return;
        }
        for (const d of driven) {
            if (d.group.targetedAnimations.length === 0) continue;
            const value = d.read();
            if (value === d.last) continue;
            d.last = value;
            evaluate(d.group, Math.max(d.stamp.inputMin, Math.min(value, d.stamp.inputMax)));
        }
    };
    // After Babylon's animation step: the Timeline places its clips before it
    // (`onBeforeAnimationsObservable`), so the bound weights read here are
    // this frame's, seeks included.
    const scene = object.babylonScene;
    const observer = scene.onAfterAnimationsObservable.add(pass);
    passes.set(object, () => scene.onAfterAnimationsObservable.remove(observer));
}
